function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import clsx from 'clsx';
import omit from 'lodash/omit';
import camelCase from 'lodash/camelCase';
import { motion } from 'motion/react';
import React, { forwardRef } from 'react';
import produce from 'immer';
import { ButtonReact } from 'common/components/button';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { prompt } from 'common/components/confirm';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { MetricUnitsButton } from 'common/query-builder/chart-customization/metric-units';
import { CallingLocation } from 'common/query-builder/chart-customization/tracking';
import { ControlBarAction, ControlBarActionItems } from 'iron/common/query-builder/query-entry-section/constants';
import { DataGroupSelect } from 'common/query-builder/data-group-select';
import { EntryRowProps } from 'common/query-builder/query-entry-section/row-types/types';
import { Action, MetricEntry } from 'common/query-builder/query-entry-section/types';
import { MetricType, TopLevelMetricType } from 'common/types/reports/bookmark';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useMetricEntries } from 'common/report/insights/contexts/metrics';
import { assert } from 'common/util';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { hasSaveableBehavior } from 'common/report/insights/contexts/utils/behaviors';
import { DisplaySize } from 'common/components/tooltip/types';
import { NewTooltip } from 'common/components/tooltip/new-tooltip';
import styles from './index.module.styl';
// eslint-disable-next-line import/no-cycle
import { ActiveMenu, MetricParts, RowProps } from './types/catalog';
import { OverflowControls } from './common/controls';
import { MetricSelector } from './common/metric-selector';
// eslint-disable-next-line import/no-cycle
import { MetricBehaviorBase } from './types/base';
import { checkUpsellForMetric } from './utils';
import { ValidMetricTypes } from './types/constants';
type NewMetricEntryProps = RowProps & {
  isExpanded?: boolean;
  /** when provided, allows for a drag and drop gripper element */
  handleRef?: (element: Element | null) => void;
};

/**
 * MetricEntry renders every possible metric type Insights supports. Within this type of metric
 * you can have saved or unsaved behavior definitions (funnel, retention or simple behaviors).
 *
 * All entity management interactions like saving, renaming, etc... are handled in this component.
 *
 * @see https://www.notion.so/mxpnl/Saved-Formulas-Tech-Design-961190b1eaa34cfd9907d57d0d6b2f49?pvs=4
 */
export const NewMetricEntry = forwardRef<HTMLDivElement, NewMetricEntryProps>(function (props, ref) {
  var _props$isExpanded, _useCurrentProject, _MetricParts$metricTy, _MetricParts$metricTy2, _MetricParts$metricTy3, _props$entry$behavior;
  const [isExpanded, setIsExpanded] = React.useState((_props$isExpanded = props.isExpanded) !== null && _props$isExpanded !== void 0 ? _props$isExpanded : true);
  const [activeMenu, _setActiveMenu] = React.useState<ActiveMenu>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const {
    saveMetric,
    resetMetric,
    getSaveMetricDisabledMessage,
    saveBehavior,
    getClause
  } = useMetricEntries();
  const metrics = useMetrics();
  const {
    t
  } = useTranslation();
  const currentProject = (_useCurrentProject = useCurrentProject()) === null || _useCurrentProject === void 0 || (_useCurrentProject = _useCurrentProject.get()) === null || _useCurrentProject === void 0 ? void 0 : _useCurrentProject.value;
  const hasMetricLevelDataGroups = props.sectionProps.metricLevelDataGroups;
  const checkUpsell = checkUpsellForMetric(currentProject);
  const metric = props.entry;
  const behavior = metric.behavior;
  const isFormulaEntry = props.entry.type === TopLevelMetricType.NewFormulaEntry;
  const metricType = (isFormulaEntry ? MetricType.Formula : behavior.type) as ValidMetricTypes;
  const isPeopleBasedMetric = [MetricType.People, MetricType.Cohort].includes(metricType);
  assert(metricType in MetricParts, "attempted to render an unsupported metric type");
  const MetricMeasurement = MetricParts[metricType].measurement;
  const MetricBehavior = MetricParts[metricType].definition;

  /** any change to a saved metric will result in the save button being enabled */
  const updateMetric = (updates: Partial<MetricEntry>) => {
    props.updateEntry(_objectSpread(_objectSpread({}, props.entry), updates));
  };

  /** augmented props to pass on to child components */
  const childProps: RowProps = _objectSpread(_objectSpread({}, omit(props, "ref", "handleRef")), {}, {
    updateEntry: updateMetric,
    activeMenu,
    setActiveMenu: menu => _setActiveMenu(menu)
  });

  /** computes any per-metric type custom actions, like customize buckets for simple metrics, or SCD property actions */
  const customMetricActions = (_MetricParts$metricTy = (_MetricParts$metricTy2 = (_MetricParts$metricTy3 = MetricParts[metricType]).customActions) === null || _MetricParts$metricTy2 === void 0 ? void 0 : _MetricParts$metricTy2.call(_MetricParts$metricTy3, childProps)) !== null && _MetricParts$metricTy !== void 0 ? _MetricParts$metricTy : [];
  const hasUnsavedChanges = props.entry.id ? props.entry.hasUnsavedChanges : false;

  /** we only want to display the behavior block when working with saved behaviors */
  const canSaveBehavior = hasSaveableBehavior(getClause({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex
  }));
  const shouldDisplayBehaviorControls = ((_props$entry$behavior = props.entry.behavior) === null || _props$entry$behavior === void 0 ? void 0 : _props$entry$behavior.id) && canSaveBehavior;
  const canAlterEntries = !props.sectionProps.disableAddEntry;
  const updateDisabledMessage = getSaveMetricDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex
  });
  const savingDisabledMessage = getSaveMetricDisabledMessage({
    index: props.index,
    parentMetricIndex: props.parentMetricIndex,
    omitPermissionsCheck: true
  });
  const handleSaveMetric = async function handleSaveMetric() {
    let saveAsNew = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (checkUpsell.shouldUpsell) {
      checkUpsell.upsellAction();
      return;
    }
    setIsSaving(true);
    await saveMetric({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew
    });
    setIsSaving(false);
  };
  const handleSaveBehavior = async function handleSaveBehavior() {
    let saveAsNew = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    if (checkUpsell.shouldUpsell) {
      return checkUpsell.upsellAction();
    }
    setIsSaving(true);
    await saveBehavior({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex,
      saveAsNew
    });
    setIsSaving(false);
  };
  const handleCollapse = () => setIsExpanded(!isExpanded);
  const handleHideMetric = () => updateMetric({
    isHidden: !props.entry.isHidden
  });
  const handleRemoveMetric = () => props.removeEntry();
  const handleResetMetric = () => {
    resetMetric({
      index: props.index,
      parentMetricIndex: props.parentMetricIndex
    });
  };

  /** detach from saved entity when cloning a metric */
  const handleDuplicateMetric = () => props.cloneEntry(_objectSpread(_objectSpread({}, props.entry), {}, {
    _idx: undefined,
    id: null,
    name: metric.name ? "".concat(metric.name, " (").concat(t("common.copy"), ")") : undefined,
    isExpanded: true,
    isHidden: false
  }));
  const handleRename = async () => {
    const promptResponse = await prompt({
      /** @todo(metrics) plug in automatic naming here */
      'initial-input-value': metric.name || t("query-builder.metrics.new-definition"),
      'allow-empty-input-value': false,
      'show-input': true
    }, {
      inputValidator(stagedInput) {
        const trimmed = stagedInput.trim();
        const labelIsUnchanged = trimmed === metric.name;
        const isUnique = metrics.getAllValues().filter(value => value.id !== props.entry.id).every(value => value.name !== trimmed);
        return labelIsUnchanged || isUnique ? null : t("components.create-entity.errors.name-exists");
      }
    }, {
      title: _ref => {
        let {
          t
        } = _ref;
        return t("query-builder.actions.rename");
      },
      confirmButtonLabel: _ref2 => {
        let {
          t
        } = _ref2;
        return t("common.done");
      }
    });
    if (promptResponse.didConfirm) {
      const name = promptResponse.inputValue.trim();
      updateMetric({
        name,
        userNamed: Boolean(name)
      });
    }
  };
  return <div className={clsx(styles.entry, styles[camelCase("is-".concat(metricType, "-metric"))], {
    [styles.isHidden]: props.entry.isHidden
  })}>
      {/* metric header area */}
      <div ref={ref} className={styles.header}>
        {/* drag and rop gripper */}
        {props.handleRef && <NewTooltip content='Drag to move' size={DisplaySize.Small} placement='top'>
            <div className={styles.gripper}>
              <SvgIconReact icon='gripper-xs' />
            </div>
          </NewTooltip>}

        {/* icon/collapse button */}
        <ButtonReact className={clsx(styles.collapseButton, styles.indicator, {
        [styles.hasUnsavedChanges]: hasUnsavedChanges
      })} theme={ButtonTheme.Tertiary} onClick={handleCollapse}>
          <span className={styles.label}>{props.rowLabel}</span>
          <SvgIconReact className={styles.caret} icon='caret-down' />
        </ButtonReact>

        <div className={styles.content}>
          <MetricSelector {...childProps} />
        </div>

        {/* indicate sync status  */}
        {isSaving && <span className={styles.status}>{t("common.saving_suspense")}</span>}

        {/* metric controls */}
        <div className={styles.actions}>
          {canAlterEntries && <ButtonReact theme={ButtonTheme.Tertiary} icon='x' onClick={handleRemoveMetric} />}
          {/* metric overflow controls for saving, resetting, etc... */}
          <OverflowControls items={[{
          items: [
          // show "Update Metric" only when saved and with local changes
          metric.id && metric.hasUnsavedChanges && {
            label: _ref3 => {
              let {
                t
              } = _ref3;
              return t("query-builder.metrics.update");
            },
            icon: "push-up",
            onSelect: () => handleSaveMetric(),
            isDisabled: Boolean(updateDisabledMessage),
            tooltip: updateDisabledMessage ? {
              text: updateDisabledMessage
            } : null
          },
          // show "Save Metric" only when unsaved
          // Don't show for Retention Frequency metrics
          !metric.id && metricType !== MetricType.RetentionFrequency && _objectSpread(_objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.SaveMetric]), {}, {
            onSelect: () => handleSaveMetric()
          }, checkUpsell.shouldUpsell && {
            icon: "brand-upgrade"
          }), {}, {
            isDisabled: Boolean(savingDisabledMessage),
            tooltip: savingDisabledMessage ? {
              text: savingDisabledMessage
            } : null
          }),
          // show "Save Behavior" only when working with metrics with unsaved behaviors
          canSaveBehavior && props.entry.behavior && !props.entry.behavior.id && _objectSpread({
            label: "Save Behavior",
            icon: "save",
            onSelect: () => handleSaveBehavior()
          }, checkUpsell.shouldUpsell && {
            icon: "brand-upgrade"
          }),
          // show "Save as New Metric" only when rendering a saved metric
          metric.id && _objectSpread(_objectSpread({
            label: _ref4 => {
              let {
                t
              } = _ref4;
              return t("common.save-as-new");
            },
            icon: "save",
            onSelect: () => handleSaveMetric(true)
          }, checkUpsell.shouldUpsell && {
            icon: "brand-upgrade"
          }), {}, {
            isDisabled: Boolean(savingDisabledMessage),
            tooltip: savingDisabledMessage ? {
              text: savingDisabledMessage
            } : null
          }),
          // show "Reset" only when dealing with a saved metric with local changes
          metric.id && metric.hasUnsavedChanges && {
            label: _ref5 => {
              let {
                t
              } = _ref5;
              return t("common.reset");
            },
            icon: "undo",
            onSelect: handleResetMetric
          }, // rename metric
          _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Rename]), {}, {
            onSelect: handleRename
          }),
          // any metric custom actions
          ...customMetricActions,
          // hide metric
          canAlterEntries && !props.disableHideEntry && _objectSpread(_objectSpread({}, ControlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide]), {}, {
            onSelect: handleHideMetric
          }),
          // clone metric
          canAlterEntries && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Duplicate]), {}, {
            onSelect: handleDuplicateMetric
          }),
          // remove metric
          canAlterEntries && props.removeEntry && _objectSpread(_objectSpread({}, ControlBarActionItems[ControlBarAction.Delete]), {}, {
            onSelect: handleRemoveMetric
          })].filter(Boolean)
        }]} />
        </div>
      </div>

      {/* metric body */}
      <motion.div className={styles.body} transition={{
      type: "spring",
      visualDuration: 0.1,
      bounce: 0.3
    }} initial={isExpanded} animate={isExpanded ? "expanded" : "collapsed"} variants={{
      expanded: {
        height: "auto"
      },
      collapsed: {
        height: 0,
        overflow: "hidden"
      }
    }}>
        <div className={styles.content}>
          {/* formula metrics do not render a behavior block */}
          {shouldDisplayBehaviorControls ? <MetricBehaviorBase {...childProps} behavior={props.entry.behavior} metricType={metricType} updateEntry={updateMetric}>
              <MetricBehavior {...childProps} />
            </MetricBehaviorBase> : <MetricBehavior {...childProps} />}
        </div>

        {/* metric measurement options */}
        <div className={styles.measurement}>
          {/* metric units to display a result like a percent, currency or number */}
          <MetricUnitsButton callingLocation={CallingLocation.Metric} chartType={props.currentChartType} entry={props.entry} size={ButtonSize.Small} setEntry={entry => props.updateEntry(entry as MetricEntry, {
          requery: false
        })} />

          <div className={styles.options}>
            {/* data group selector */}
            {hasMetricLevelDataGroups && props.entry.type !== TopLevelMetricType.NewFormulaEntry && <DataGroupSelect selectedDataGroup={isPeopleBasedMetric ? behavior.dataGroupId : metric.measurement.dataGroupId} onSelect={id => {
            const updatedEntry = produce(props.entry, draft => {
              if (isPeopleBasedMetric) {
                draft.behavior.dataGroupId = id;
              } else {
                draft.measurement.dataGroupId = id;
              }
            });
            props.updateEntry(updatedEntry);
          }} {...metricType === MetricType.Cohort ? {
            readonly: true,
            disabledReason: t("data-groups.analyze-by-cohort-group")
          } : {}} />}
            <MetricMeasurement {...childProps} />
          </div>
        </div>
      </motion.div>
    </div>;
});

/** For QueryEntrySection */
export const NewMetricEntryRow = forwardRef<HTMLDivElement, EntryRowProps<MetricEntry>>(function (props, ref) {
  return <NewMetricEntry {...props} ref={ref} updateEntry={(entry, opts) => {
    props.updateEntry(Action.Edit, entry, opts);
  }} cloneEntry={entry => {
    props.updateEntry(Action.Clone, entry);
  }} removeEntry={() => props.removeEntry()} />;
});